import React, { useEffect, useState } from "react";
import { sectionFrame } from "../../config/site";
import { assets, conferenceHighlights } from "../../content/site";
import { trackEvent } from "../../lib/analytics";

export function HeroSection() {
    const [shouldLoadFigure, setShouldLoadFigure] = useState(false);
    const [isFigureReady, setIsFigureReady] = useState(false);

    const figureClassName = "absolute inset-0 h-full w-full object-contain mix-blend-screen";

    useEffect(() => {
        const scheduleLoad = () => {
            if (typeof window.requestIdleCallback === "function") {
                const idleId = window.requestIdleCallback(
                    () => {
                        globalThis.setTimeout(() => setShouldLoadFigure(true), 600);
                    },
                    { timeout: 2500 }
                );

                return () => {
                    window.cancelIdleCallback(idleId);
                };
            }

            const timeout = globalThis.setTimeout(() => setShouldLoadFigure(true), 1800);

            return () => {
                globalThis.clearTimeout(timeout);
            };
        };

        if (document.readyState === "complete") {
            return scheduleLoad();
        }

        let cleanupTimeout: () => void = () => {};

        const handleLoad = () => {
            cleanupTimeout = scheduleLoad();
        };

        window.addEventListener("load", handleLoad, { once: true });

        return () => {
            window.removeEventListener("load", handleLoad);
            cleanupTimeout();
        };
    }, []);

    return (
        <section className="relative isolate px-4 pb-16 pt-14 sm:px-6 sm:pt-20 lg:pb-24 lg:pt-24">
            <div className={sectionFrame}>
                <div className="grid items-center gap-8 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
                    <div className="relative z-20 min-w-0">
                        <p className="mb-6 text-sm font-medium uppercase tracking-[0.12em] text-accent sm:text-lg">
                            Прошла 18 апреля 2026 года · Якутск
                        </p>
                        <h1 className="max-w-[1050px] text-[clamp(1.8rem,4.8vw,4.3rem)] font-bold uppercase leading-[1.04] tracking-[-0.04em]">
                            Крупнейшая{" "}
                            <span className="inline-block whitespace-nowrap text-accent">
                                ИТ-конференция
                            </span>{" "}
                            на Дальнем Востоке России
                        </h1>
                        <a
                            href="#recap"
                            className="mt-8 inline-flex min-h-[48px] items-center justify-center rounded-full border border-accent bg-accent px-7 py-3 text-base font-semibold text-black transition hover:bg-[#abff40] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent sm:text-lg"
                            onClick={() => trackEvent("cta_recap_click", { source: "hero" })}
                        >
                            Как это было{" "}
                            <span className="ml-4" aria-hidden="true">
                                ↗
                            </span>
                        </a>
                    </div>
                    <div
                        className="pointer-events-none relative z-10 aspect-square w-full max-w-[520px] justify-self-end max-lg:hidden"
                        aria-hidden="true"
                    >
                        <img
                            src={assets.figure1Poster}
                            alt=""
                            className={`${figureClassName} transition-opacity duration-300 ${isFigureReady ? "opacity-0" : "opacity-100"}`}
                            decoding="async"
                        />
                        {shouldLoadFigure ? (
                            <img
                                src={assets.figure1}
                                alt=""
                                className={`${figureClassName} transition-opacity duration-300 ${isFigureReady ? "opacity-100" : "opacity-0"}`}
                                decoding="async"
                                onLoad={() => setIsFigureReady(true)}
                            />
                        ) : null}
                    </div>
                </div>
                <dl className="relative z-[2] mt-12 grid grid-cols-2 gap-x-5 gap-y-8 border-t border-white/25 pt-8 sm:mt-16 sm:gap-10 lg:grid-cols-3">
                    {conferenceHighlights.map(({ value, label }) => (
                        <div key={label}>
                            <dt className="text-[clamp(2.5rem,6vw,4.5rem)] font-bold leading-none tracking-[-0.05em] text-accent">
                                {value}
                            </dt>
                            <dd className="mt-3 max-w-[330px] text-sm leading-relaxed text-white/85 sm:text-lg">
                                {label}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </section>
    );
}
